import {
    Article, ArticleCategoryGroup,
    ArticleRequest,
    PowderguideApiInterface
} from './powderguide-api'
import { injectAd, InjectedAd } from './ad-injector'
import { PaginationController } from './pagination-controller'
import { UrlHashHandler } from './url-hash-handler'

// A function rather than a frozen object: freezing is shallow, and a shared
// `categories` array would be mutated by toggleCategory().
const defaultRequest = (): ArticleRequest => ({
    page: 1,
    page_size: 24,
    categories: [ ],
    search: '',
});

export class PowderguideArticles {
    public loading: boolean = true;
    public error: string|null = null;
    public articles: Article[];
    public content: (Article|InjectedAd)[];
    public adEvery: number = 12;
    public adOffset: number = 2;
    public readonly pagination: PaginationController = new PaginationController({
        page: 1,
        next_page: 2,
        previous_page: null,
        last_page: 1,
        per_page: 24,
        from_item: 0,
        to_item: 24,
        total_items: 0,
    }, { startFixedElements: 2, endFixedElements: 2, elementsAroundCurrent: 2 }, () => this.loadPage(this.pagination.page));
    public request: ArticleRequest = defaultRequest();
    public categories: ArticleCategoryGroup[];
    private hashHandler: UrlHashHandler<[ 'powderguide_articles[page]', 'powderguide_articles[categories]', 'powderguide_articles[search]' ]> = new UrlHashHandler([ 'powderguide_articles[page]', 'powderguide_articles[categories]', 'powderguide_articles[search]' ]);

    constructor(
        private api: PowderguideApiInterface,
        Alpine: any
    ) {
        this.categories = Alpine.reactive([ ]);
        this.articles = Alpine.reactive([ ]);
        this.content = Alpine.reactive([ ]);
    }


    public async init(config: Partial<{ paginate: number, adEvery: number, adOffset: number }> = { }): Promise<void> {
        if (config.paginate) {
            this.pagination.information.per_page = config.paginate;
            this.request.page_size = config.paginate;
        }
        this.adEvery = config.adEvery ?? this.adEvery;
        this.adOffset = config.adOffset ?? this.adOffset;
        this.readFromHash();
        await this.updateArticles();
    }

    public async loadPage(page: number): Promise<void> {
        this.request.page = page;
        await this.updateArticles();
        this.writeToHash();
    }

    private async updateArticles(): Promise<void> {
        try {
            this.loading = true;
            this.error = null;
            const result = await this.api.fetchArticles(this.request);
            this.articles.length = 0;
            this.articles.push(...result.data);
            this.content.length = 0;
            this.content.push(...injectAd(result.data, this.adEvery, this.adOffset));
            this.pagination.update(result.pagination);
            this.categories = result.categories;
            this.request.page = result.pagination.page;
            this.request.page_size = result.pagination.per_page;
            this.writeToHash();
        } catch (e) {
            this.error = e.message;
        } finally {
            this.loading = false;
        }
    }

    public get filtersCustomized(): boolean {
        return this.request.categories.length > 0 || this.request.search !== '';
    }

    public async clearFilter(): Promise<void> {
        // Keep the page size the list was initialised with.
        this.request = { ...defaultRequest(), page_size: this.request.page_size };
        await this.updateArticles();
        this.writeToHash();
    }

    public hasCategory(category: string): boolean {
        return this.request.categories.indexOf(category) !== -1;
    }

    public async toggleCategory(category: string): Promise<void> {
        if (this.hasCategory(category)) {
            this.request.categories = this.request.categories.filter(c => c !== category);
        } else {
            this.request.categories.push(category);
        }

        this.request.page = 1;
        await this.updateArticles();
        this.writeToHash();
    }

    private readFromHash(): void {
        const properties = this.hashHandler.read();
        if (properties['powderguide_articles[page]']) {
            this.request.page = parseInt(properties['powderguide_articles[page]']);
        }
        if (properties['powderguide_articles[categories]']) {
            this.request.categories = properties['powderguide_articles[categories]'].split(',').map(c => c.trim()).filter(c => c.length > 0);
        }
        if (properties['powderguide_articles[search]']) {
            this.request.search = properties['powderguide_articles[search]'];
        }
    }

    private writeToHash(): void {
        this.hashHandler.write({
            'powderguide_articles[page]': this.request.page > 1 ? this.request.page.toString() : null,
            'powderguide_articles[categories]': this.request.categories.join(','),
            'powderguide_articles[search]': this.request.search,
        });
    }
}
